import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { resolveTextDirection } from '@/utils/helpers/language.helper';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 450px;

  .p-dialog-content {
    padding: var(--default-padding) var(--big-padding);
  }

  .p-dialog-footer {
    padding: var(--big-padding);
    padding-top: 0;
  }
`;

type UnsavedChangesModalProps = {
  visible: boolean;
  onHide: () => void;
  onSave: () => void;
  onDiscard: () => void;
  hasErrors?: boolean;
  message?: string;
  title?: string;
  saveButtonLabel?: string;
  discardButtonLabel?: string;
  discardButtonType?: 'text' | 'outlined';
  hideSaveIcon?: boolean;
  hideDiscardButton?: boolean;
};

export const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  visible,
  onHide,
  onSave,
  onDiscard,
  hasErrors,
  message,
  title,
  saveButtonLabel,
  discardButtonLabel,
  discardButtonType = 'text',
  hideSaveIcon = false,
  hideDiscardButton = false,
}) => {
  const { t, i18n } = useTranslation();
  const resolvedDir = resolveTextDirection(i18n.language);

  const handleSave = () => {
    onSave();
    onHide();
  };

  const handleDiscard = () => {
    onDiscard();
    onHide();
  };

  const footer = (
    <FlexContainer justify="flex-start" gap={12}>
      <span className="submit-button">
        <AppButton
          label={
            saveButtonLabel || t('content.quiz.unsavedChanges.confirm.save')
          }
          icon={hideSaveIcon ? undefined : 'pi pi-check'}
          severity="secondary"
          isDisabled={hasErrors}
          onClick={handleSave}
        />
      </span>
      {hasErrors && (
        <Tooltip
          target=".submit-button"
          content={t('generic.requiredFields')}
          position="top"
        />
      )}
      {!hideDiscardButton && (
        <AppButton
          label={
            discardButtonLabel ||
            t('content.quiz.unsavedChanges.confirm.discard')
          }
          severity="secondary"
          type={discardButtonType}
          onClick={handleDiscard}
        />
      )}
    </FlexContainer>
  );

  return (
    <StyledDialog
      pt={{ root: { dir: resolvedDir } }}
      draggable={false}
      visible={visible}
      header={title || t('dialog.unsavedChanges')}
      modal
      footer={footer}
      onHide={onHide}
      className="v2"
      closable
    >
      <span>{message || t('content.quiz.unsavedChanges.confirm')}</span>
    </StyledDialog>
  );
};
